import { isEmpty } from 'radash';
import { useController } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Input } from '@/components/core/Input';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useValidationsStore, Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';

interface IntegerComponentProps extends EditorComponent {
    // Integer specific props go here
    isDimensional?: boolean;
    className?: string;
}

const IntegerComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    gridMode = false,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    hidden = false,
    readonly = false,
    hideLabel = false,
    isKeyAttribute = false,
    isDimensional = false,
    beforeAttributeControl,
    className,
    handleUrlChange,
    onBlur: parentOnBlur
}: IntegerComponentProps) => {

    const focusRef = useFocusOnAttribute<HTMLInputElement>(attribute);

    const placeholder = usePlaceholder(attribute, readonly);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { field } = useController({
        name: fieldPath,
    });

    if (hidden) {
        return null;
    }

    const setRef = (e: HTMLInputElement | null) => {
        if (e) {
            (focusRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
        }
        field.ref(e);
    };

    const handleBlur = () => {
        field.onBlur();
        if (parentOnBlur) {
            parentOnBlur();
        }
    };

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!gridMode && !readonly && !isDimensional}
        >
            <div className="tw-flex tw-flex-row tw-items-center tw-relative">
                <Input
                    id={fieldPath}
                    borderless={gridMode}
                    placeholder={placeholder}
                    onClick={handleUrlChange}
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={handleBlur}
                    disabled={readonly}
                    ref={setRef}
                    gridMode={gridMode}
                    className={`tw-h-9 ${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-white ${className ? className: ''}`} />
            </div>
        </Layout>
    );
};

export { IntegerComponent };
